import { Alerts, Typography } from '@loophealth/loop-ui-web-library';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronRight } from '../../../assets/img/CustomIcons';
import { setModularSelectedBasePlan } from '../../../redux/slices/ModularEnrolmentSlice';
import {
    selectModularPlans,
    selectModularSelectedBasePlan,
    selectModularWalletBalance
} from '../../../redux/slices/ModularEnrolmentSlice/selectors';
import { IModule } from '../../../redux/slices/PolicyListSlice/types';
import { SEGMENT_ACTIONS } from '../../../utils/constants/SegmentActionConstants';
import useSegment from '../../../utils/hooks/useSegment';
import { Loader } from '../../atoms';
import ScrollToTop from '../../atoms/ScrollToTop';
import usePremiumBreakdown from '../ModularEnrolmentFooter/hooks/usePremiumBreakdown';
import ModularEnrolmentHeader from '../ModularEnrolmentHeader';
import ModularPlanItem from './ModularPlanItem';
import {
    StyledHeaderContainer,
    StyledModularPlanNavigationArrow,
    StyledModularPlanNavPages,
    StyledModularPlansContainer,
    StyledModularPlansList,
    StyledModularPlansNavigation
} from './styles';
import { getMaxPropertiesToShow } from './utils';
import useModulerEnrolmentContext from '../../pages/SPAContainer/pages/ModularEnrolmentPage/ModulerEnrolmentContext';
import { selectUserData } from '../../pages/LoginContainer/redux/slices/UserSlice/selectors';
import { fetchPlanRecommendation } from '../../../adapters';
import { setAmplitudeUserProperties } from '../../../utils/integrations/amplitude';
import initializeTidio, { identifyTidioUser } from '../../../utils/integrations/tidio';

const ModularPlansContainer: React.FC = () => {
    const dispatch = useDispatch();
    const trackClick = useSegment('click');
    const trackPage = useSegment('page');
    const scrollRef = useRef<HTMLDivElement>(null);
    const basePlansData = useSelector(selectModularPlans);
    const selectedBasePlan = useSelector(selectModularSelectedBasePlan);
    const walletAmount = useSelector(selectModularWalletBalance);
    const [scrollPercentage, setScrollPercentage] = React.useState(0);
    const [flexDownBalance, setFlexDownBalance] = React.useState(0);
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const { modular: prevPlan, priceSummary } = usePremiumBreakdown();
    const { modularAnimationShown, setModularAnimationShown } = useModulerEnrolmentContext();
    const userId = useSelector(selectUserData).data.userId ?? '';
    const userData = useSelector(selectUserData).data;
    const companyDetails = useSelector((state: any) => state?.policyList?.companyDetails?.data);

    const isLoading = basePlansData.loading;
    const errorMessage = basePlansData.error?.message;

    const plans = basePlansData.data ?? [];
    const totalPlans = plans.length;
    const [recommendedPlanName, setRecommendedPlanName] = React.useState<string | null>(null);
    const [hasPagination, setHasPagination] = React.useState(false);
    const [hasPrev, setHasPrev] = React.useState(false);
    const [hasNext, setHasNext] = React.useState(false);

    const maxCoveredBenefits = useMemo(() => getMaxPropertiesToShow(plans), [plans]);

    const setCurrentBasePlan = (id: string) => {
        if (selectedBasePlan != id && walletAmount) {
            const currentPlan = plans.find((p) => p.id === id);
            const newPrice = priceSummary.totalPremium - (prevPlan?.price || 0) + (currentPlan?.price || 0);

            if (
                prevPlan &&
                currentPlan &&
                currentPlan.price < prevPlan.price &&
                currentPlan.price < walletAmount &&
                newPrice < walletAmount
            ) {
                const freed = walletAmount - newPrice;
                setFlexDownBalance(freed);
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => setFlexDownBalance(0), 10000);
            } else {
                setFlexDownBalance(0);
            }
        }
        dispatch(setModularSelectedBasePlan(id));
    };

    const handleSelectPlan = (plan: IModule) => {
        const segmentAction = SEGMENT_ACTIONS.CLICK.CHOOSE_PLAN;
        trackClick({
            name: segmentAction.name,
            properties: {
                ...segmentAction.properties,
                plan_id: plan.id,
                plan_name: plan.name,
                sum_insured: plan.sumInsured,
                eligible_members: plan.familyDefinition
            }
        });
        window.plotline('track', 'Clicked_choose_plan', { benefit_id: plan.id, plan: plan.name, userId });
        setCurrentBasePlan(plan.id);
    };

    const handleNextNavClick = () => {
        if (!scrollRef.current) return;
        const left = scrollRef.current.scrollLeft + scrollRef.current.offsetWidth / 2;
        scrollRef.current.scrollTo({ left, behavior: 'smooth' });
    };

    const handlePrevNavClick = () => {
        if (!scrollRef.current) return;
        const left = Math.max(0, scrollRef.current.scrollLeft - scrollRef.current.offsetWidth / 2);
        scrollRef.current.scrollTo({ left, behavior: 'smooth' });
    };

    const handleScroll = useCallback(() => {
        if (!scrollRef.current) return;

        const scrollLeft = scrollRef.current.scrollLeft;
        const width = scrollRef.current.offsetWidth;
        const scrollWidth = scrollRef.current.scrollWidth;
        setScrollPercentage(Math.floor((scrollLeft / (scrollWidth - width)) * 100));
        setHasPagination(scrollWidth > width);
        setHasPrev(scrollLeft > 100);
        setHasNext(scrollLeft + width + 100 < scrollWidth);
    }, [scrollRef]);

    const handleOnScrollbarClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!scrollRef.current) return;
        const per = ((e.pageX - e.currentTarget.offsetLeft) / e.currentTarget.offsetWidth) * 100;
        const newScrollLeft = (scrollRef.current.scrollWidth - scrollRef.current.offsetWidth) * (per / 100);
        scrollRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    };

    useEffect(() => {
        if (scrollRef.current) {
            handleScroll();
            scrollRef.current.addEventListener('scroll', handleScroll, { passive: true });
            window.addEventListener('resize', handleScroll);
            if (!modularAnimationShown) {
                scrollRef.current.scrollTo({ left: scrollRef.current.scrollWidth, behavior: 'smooth' });
                setTimeout(() => {
                    const elementToScroll = document.getElementById(selectedBasePlan ?? '');
                    if (selectedBasePlan && elementToScroll && scrollRef.current) {
                        const left =
                            elementToScroll.offsetLeft -
                            scrollRef.current.offsetWidth / 2 +
                            elementToScroll.offsetWidth / 2;
                        scrollRef.current?.scrollTo({ left: left, behavior: 'smooth' });
                    } else {
                        scrollRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
                    }
                }, 100 * totalPlans);
                setModularAnimationShown(true);
            }
        }
        return () => {
            scrollRef.current?.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, [scrollRef.current, handleScroll, totalPlans]);

    useEffect(() => {
        if (totalPlans && !isLoading) {
            const pageAction = SEGMENT_ACTIONS.PAGE.POLICY_PLAN_SELECTION_PAGE;
            trackPage({
                name: pageAction.name,
                properties: {
                    ...pageAction.properties,
                    total_plans: plans?.length
                }
            });
            window.plotline('track', 'Viewed_policy_plan_selection_page', {
                userId,
                benefit_id: selectedBasePlan ?? ''
            });
        }
    }, [plans]);

    // Fetch plan recommendation when plans are loaded
    useEffect(() => {
        if (totalPlans && !isLoading && userId && userData && companyDetails) {
            fetchPlanRecommendation(userId)
                .then((data) => {
                    if (data.recommended_modular_benefit) {
                        // Store recommendation data globally so it can be accessed by Amplitude callback
                        const w = window as any;
                        w.__amplitudeRecommendationData = data;
                        // Update Amplitude user properties
                        // The Tidio chat callback is registered in the Amplitude engagement plugin
                        setAmplitudeUserProperties({
                            recommended_modular_benefit: data.recommended_modular_benefit,
                            employee_facing_reasons: data.employee_facing_reasons
                        });
                        setRecommendedPlanName(data.recommended_modular_benefit);
                        // Initialize Tidio only when recommendations are available
                        initializeTidio();
                        // Identify Tidio user with user data
                        const userEmail = userData?.email as string | undefined;
                        const userName = userData?.firstName && userData?.lastName
                            ? `${userData.firstName} ${userData.lastName}`.trim()
                            : undefined;
                        const userPhone = userData?.mobile as string | undefined;
                        const contactProperties: Record<string, string> = {
                            genome_user_id: userId
                        };
                        if (companyDetails?.companyName) {
                            contactProperties.company_name = companyDetails.companyName;
                        }
                        identifyTidioUser(
                            userId,
                            userEmail,
                            userName,
                            userPhone,
                            contactProperties
                        );
                    }
                }).catch(() => {/* no op */});
        }
    }, [totalPlans, isLoading, userId, userData, companyDetails]);
    return (
        <StyledModularPlansContainer>
            <ScrollToTop />
            <StyledHeaderContainer>
                <ModularEnrolmentHeader
                    title="Find the right plan for your needs"
                    subtitle={`Select from ${totalPlans} plans, which best fits you`}
                    freedBalance={flexDownBalance}
                    onFreedBalanceClose={() => setFlexDownBalance(0)}
                />
            </StyledHeaderContainer>
            {isLoading ? (
                <Loader />
            ) : errorMessage ? (
                <Alerts.StatusAlert
                    variant="over"
                    text={
                        <Typography variant="medium" color="error">
                            {errorMessage}
                        </Typography>
                    }
                    alignItems="center"
                />
            ) : (
                <StyledModularPlansNavigation>
                    <StyledModularPlansList $total={totalPlans} ref={scrollRef}>
                        {plans.map((p, i) => (
                            <ModularPlanItem
                                key={p.id}
                                plan={p}
                                index={i}
                                isSelected={selectedBasePlan === p.id}
                                onClick={() => handleSelectPlan(p)}
                                maxProperties={maxCoveredBenefits}
                                isRecommended={recommendedPlanName !== null && p.name === recommendedPlanName}
                            />
                        ))}
                    </StyledModularPlansList>

                    {hasPagination && hasPrev && (
                        <StyledModularPlanNavigationArrow $flip onClick={handlePrevNavClick}>
                            <ChevronRight />
                        </StyledModularPlanNavigationArrow>
                    )}
                    {hasPagination && hasNext && (
                        <StyledModularPlanNavigationArrow onClick={handleNextNavClick}>
                            <ChevronRight />
                        </StyledModularPlanNavigationArrow>
                    )}

                    {hasPagination && (
                        <StyledModularPlanNavPages onClick={handleOnScrollbarClick} $percent={scrollPercentage} />
                    )}
                </StyledModularPlansNavigation>
            )}
        </StyledModularPlansContainer>
    );
};

export default ModularPlansContainer;
